import React, { useEffect, useState, useRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "./Invoice.scss";
import gzavniliLogo from "./gzavnili-logo.png";
import { useParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { Returntext } from "../../../pages/Landings/common/returnText";
import { useContent, useLanguages } from "../../../hooks/content";
import BtnStandardIconText from "../../../ds/BtnStandardIconText/BtnStandardIconText";
import { ReactComponent as DownloadIcon } from "../../../icons/download3.svg";

/**
 * cropCanvas:
 * Trims away pure-white (or near-white) margins from the canvas edges.
 * Adjust 'tolerance' if your background isn't perfectly white (0 = exact white).
 */
function cropCanvas(canvas, tolerance = 0) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height).data;

  let top = null,
    left = null,
    right = null,
    bottom = null;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = (y * width + x) * 4;
      const r = imageData[index];
      const g = imageData[index + 1];
      const b = imageData[index + 2];
      const a = imageData[index + 3];

      // If this pixel is NOT pure white (within tolerance), update boundaries
      if (
        !(
          r >= 255 - tolerance &&
          g >= 255 - tolerance &&
          b >= 255 - tolerance &&
          a === 255
        )
      ) {
        if (top === null) top = y;
        if (left === null || x < left) left = x;
        if (right === null || x > right) right = x;
        bottom = y;
      }
    }
  }

  // If the entire canvas was white, return the original (no cropping)
  if (top === null) return canvas;

  const cropWidth = right - left + 1;
  const cropHeight = bottom - top + 1;

  const croppedCanvas = document.createElement("canvas");
  croppedCanvas.width = cropWidth;
  croppedCanvas.height = cropHeight;
  const croppedCtx = croppedCanvas.getContext("2d");

  croppedCtx.drawImage(
    canvas,
    left,
    top,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight
  );

  return croppedCanvas;
}

const Invoice = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const access_token = Cookies.get("access_token");
  const userData = JSON.parse(localStorage.getItem("user"));
  const { lang } = useLanguages();
  const { contentManagementData } = useContent();

  // This ref includes the ENTIRE invoice (including the button):
  const invoiceRef = useRef(null);

  // Fetch data
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/transactions/invoice/${id}?accessToken=${access_token}`
        );
        setInvoice(response.data);
      } catch (err) {
        setError(err.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id, access_token]);

  // Handle PDF Download
  const handleDownloadPDF = async () => {
    try {
      // 1) Capture the invoice, ignoring elements with class "btn-standard-icon-text"
      const originalCanvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        ignoreElements: (element) =>
          element.classList?.contains("btn-standard-icon-text"),
      });

      // 2) Crop out any pure-white margins
      const croppedCanvas = cropCanvas(originalCanvas, 0 /* tolerance */);

      // 3) Convert cropped canvas to an image
      const imgData = croppedCanvas.toDataURL("image/png");

      // 4) Create a multi-page PDF (A4)
      const pdf = new jsPDF("p", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Scale cropped canvas to fit PDF width
      const ratio = pdfWidth / croppedCanvas.width;
      const scaledWidth = pdfWidth;
      const scaledHeight = croppedCanvas.height * ratio;

      let remainingHeight = scaledHeight;
      let positionY = 0;

      // Place the entire scaled image on the first page
      pdf.addImage(imgData, "PNG", 0, 0, scaledWidth, scaledHeight);
      remainingHeight -= pdfHeight;

      // 5) Add more pages if the image is taller than one page
      while (remainingHeight > 0) {
        positionY = scaledHeight - remainingHeight; // how much is "above" the new page
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, -positionY, scaledWidth, scaledHeight);
        remainingHeight -= pdfHeight;
      }

      // 6) Download the PDF
      pdf.save(`gzavnili_invoice${invoice?.invoice_number}.pdf`);
    } catch (err) {
      console.error("Error generating PDF:", err);
    }
  };

  return (
    <div className="invoice" ref={invoiceRef}>
      <div className="cont">
        {/* HEADER */}
        <div className="header">
          <div className="left">
            <div className="top">
              <div className="mt">
                {Returntext(contentManagementData, "invoice_name", lang)}
              </div>
              <div className="st">{invoice?.invoice_number}</div>
            </div>
            <div className="bottom">
              <div className="i-date">
                {Returntext(contentManagementData, "invoice_date", lang)}{" "}
                <div className="bld">
                  {(() => {
                    const d = new Date(invoice?.created_at);
                    return `${d.getDate()} ${Returntext(
                      contentManagementData,
                      d.toLocaleString("default", {
                        month: "short",
                      }),
                      lang
                    )}, ${d.getFullYear()}`;
                  })()}
                </div>
              </div>
            </div>
          </div>
          <img src={gzavniliLogo} alt="Gzavnili Logo" className="right" />
        </div>

        {/* BODY */}
        <div className="body">
          <div className="ff">
            <div className="left">
              <div className="ttxt">
                {" "}
                {Returntext(contentManagementData, "invoice_buyer", lang)}
              </div>
              <div className="info-b-c">
                <div className="info-b">
                  <div className="left-b">
                    {" "}
                    {Returntext(contentManagementData, "invoice_name_n", lang)}
                  </div>
                  <div className="right-b">{userData.company_info}</div>
                </div>
                <div className="info-b">
                  <div className="left-b">
                    {" "}
                    {Returntext(contentManagementData, "invoice_phone", lang)}
                  </div>
                  <div className="right-b">{userData.phone}</div>
                </div>
                <div className="info-b">
                  <div className="left-b">
                    {" "}
                    {Returntext(contentManagementData, "invoice_email", lang)}:
                  </div>
                  <div className="right-b">{userData.email}</div>
                </div>
                <div className="info-b">
                  <div className="left-b">
                    {Returntext(contentManagementData, "invoice_room", lang)}
                  </div>
                  <div className="right-b">{userData.room_number}</div>
                </div>
              </div>
            </div>

            <div className="right">
              <div className="ttxt">
                {" "}
                {Returntext(contentManagementData, "invoice_seller", lang)}
              </div>
              <div className="info-b-c">
                <div className="info-b">
                  <div className="left-b">
                    {" "}
                    {Returntext(contentManagementData, "invoice_name_n", lang)}
                  </div>
                  <div className="right-b">
                    {Returntext(
                      contentManagementData,
                      "invoice_inner_name",
                      lang
                    )}
                  </div>
                </div>
                <div className="info-b">
                  <div className="left-b">
                    {" "}
                    {Returntext(
                      contentManagementData,
                      "invoice_iden_code",
                      lang
                    )}
                  </div>
                  <div className="right-b">
                    {Returntext(
                      contentManagementData,
                      "invoice_inner_identification_code",
                      lang
                    )}
                  </div>
                </div>
                <div className="info-b">
                  <div className="left-b">TBC</div>
                  <div className="right-b">
                    {Returntext(
                      contentManagementData,
                      "invoice_inner_account_num",
                      lang
                    )}
                  </div>
                </div>
                <div className="info-b">
                  <div className="left-b">BOG</div>
                  <div className="right-b">
                    {Returntext(
                      contentManagementData,
                      "invoice_inner_account_num_1",
                      lang
                    )}
                  </div>
                </div>
                <div className="info-b">
                  <div className="left-b">
                    {Returntext(contentManagementData, "invoice_mail", lang)}
                  </div>
                  <div className="right-b">
                    {Returntext(
                      contentManagementData,
                      "invoice_inner_side_code",
                      lang
                    )}
                  </div>
                </div>
                <div className="info-b">
                  <div className="left-b">
                    {" "}
                    {Returntext(contentManagementData, "invoice_phone", lang)}
                  </div>
                  <div className="right-b">
                    {Returntext(
                      contentManagementData,
                      "invoice_inner_phone",
                      lang
                    )}
                  </div>
                </div>
                <div className="info-b">
                  <div className="left-b">
                    {" "}
                    {Returntext(contentManagementData, "invoice_address", lang)}
                  </div>
                  <div className="right-b">
                    {Returntext(
                      contentManagementData,
                      "invoice_inner_address",
                      lang
                    )}{" "}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="sf">
            <div className="header-t">
              <div className="tbs">
                {" "}
                {Returntext(contentManagementData, "invoice_tracking", lang)}
              </div>
              <div className="tbs">
                {" "}
                {Returntext(contentManagementData, "invoice_weight", lang)}
              </div>
              <div className="tbs">
                {Returntext(
                  contentManagementData,
                  "invoice_transportation",
                  lang
                )}
              </div>
            </div>
            <div className="body-t">
              {invoice?.orders.map((order) => (
                <div className="tbd-wrap" key={order.tracking_number}>
                  <div className="tbd">
                    <div className="top-t">{order?.tracking_number}</div>
                    <div className="bottom-t">{order?.name}</div>
                  </div>
                  <div className="tbd">
                    {order?.weight}{" "}
                    {Returntext(contentManagementData, "invoice_KG", lang)}
                  </div>
                  <div className="tbd">{order?.price_gz} ₾</div>
                </div>
              ))}
            </div>
          </div>

          <div className="tf">
            <div className="left"></div>
            <div className="right">
              <div className="psum-cont">
                <div className="psum-l">
                  {Returntext(contentManagementData, "air_freight_fee", lang)}
                </div>
                <div className="psum-r">{invoice?.gzavniliPrice} ₾</div>
              </div>
              {invoice?.customFeeTotal > 0 && (
                <div className="psum-cont">
                  <div className="psum-l">
                    {Returntext(contentManagementData, "PBTDS", lang)}
                  </div>
                  <div className="psum-r">{invoice?.customFeeTotal}.00 ₾</div>
                </div>
              )}
              {invoice?.serviceFee > 0 && (
                <div className="psum-cont">
                  <div className="psum-l">
                    {Returntext(contentManagementData, "PBTDP", lang)}
                  </div>
                  <div className="psum-r">{invoice?.serviceFee}.00 ₾</div>
                </div>
              )}
              <div className="psum-cont">
                <div className="psum-l">
                  {Returntext(contentManagementData, "PBTD", lang)}
                </div>
                <div className="psum-r">{invoice?.transportation}.00 ₾</div>
              </div>
              <div className="psum-cont">
                <div className="psum-l">
                  {" "}
                  {Returntext(contentManagementData, "invoice_total", lang)}
                </div>
                <div className="psum-r">{invoice?.total} ₾</div>
              </div>
            </div>
          </div>
        </div>

        {/* The download button is in the same container, 
            but we'll skip it in HTML2Canvas by ignoring className="btn-standard-icon-text". */}
        <div className="btm">
          <BtnStandardIconText
            mode=""
            txt={Returntext(contentManagementData, "invoice_download", lang)}
            disabled={false}
            size="standard"
            icon={true}
            mainColor="green"
            iconPosition={"right"}
            svg={<DownloadIcon />}
            onClick={handleDownloadPDF}
          />
        </div>
      </div>
    </div>
  );
};

export default Invoice;
